/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {SettlementDetailModel} from '../models/settlement/settlement-detail/settlement-detail.model';
import {TableModel} from '../../shared/table/models/table.model';
import {TableHeaderEntryModel} from '../../shared/table/models/table-header-entry.model';
import {BundleSettlementsDetailColumnEnum} from './models/bundle-settlements-detail-column.enum';
import bundleSettlementsDetailTable from './configuration/bundle-settlements-detail-table-configuration';
import {TableLineModel} from '../../shared/table/models/table-line.model';
import {SettlementPositionMapper} from '../models/settlement-position/settlement-position.mapper';
import {SettlementPositionModel} from '../models/settlement-position/settlement-position.model';
import {TableLineDataModel} from '../../shared/table/models/table-line-data.model';
import {FlyingMenuComponent} from '../../shared/flying-menu/flying-menu.component';
import {BundleTypeEnum} from '../bundles-main-page/data/bundle-type.enum';
import * as financialDataPipes from '../util/financial-number/pipe/financial-number-data-pipe';
import {SelectedTabsService} from '../services/selected-tabs.service';
import {CurrencyBaseModel} from '../models/currency/currency-base.model';
import {FinancialNumberCellValueModel} from '../util/financial-number/model/financial-number-cell-value.model';
import {ShipToPartyBaseModel} from '../models/ship-to-party/ship-to-party-base.model';
import {PrettyPrintReferencePipe} from '../../shared/pipes/pretty-print-reference/pretty-print-reference.pipe';
import {ResizeObserver} from 'resize-observer';
import tableLineConfiguration from '../../shared/table/table-line/configuration/table-line.configuration';
import {TableService} from '../../shared/table/table-service/table.service';

@Component({
    selector: 'app-bundle-settlements-detail',
    templateUrl: './bundle-settlements-detail.component.html',
    styleUrls: ['./bundle-settlements-detail.component.css', '../styles/bundles-base.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BundleSettlementsDetailComponent<Enum, DataObject> implements OnInit, AfterViewInit, OnDestroy {
    @Input() bundleSettlementDetailData: SettlementDetailModel;
    @ViewChild('commentTableBundlesTemplate') commentTableTemplate: TemplateRef<FlyingMenuComponent>;

    readonly labelComplaintNumber = 'COMPLAINT NO.:';
    readonly labelSettlementNumber = 'SETTLEMENT:';
    readonly titleDetailInformation = 'Settlement information';
    readonly titleDetailAgreement = 'Settlement agreement';
    readonly titleDetailAgreementTable = 'Settlement agreement table';
    readonly labelCommentFlyingMenu = 'Comment';
    readonly tooltipComment = 'View comment';
    readonly tooltipNoComment = 'No comment available';
    readonly labelTotal = 'Total:';
    private readonly paddingBox = 16;
    tableData: TableModel<BundleSettlementsDetailColumnEnum, SettlementPositionModel>;
    public resizeObserver: ResizeObserver;

    get retrieveBundleType(): BundleTypeEnum {
        return this.selectedTabsService.bundleType;
    }

    get isBundleTypeTeMinutes(): boolean {
        return this.selectedTabsService.bundleType === BundleTypeEnum.TE_MINUTES_BUNDLING;
    }

    get isBundleTypePartsBundling(): boolean {
        return this.selectedTabsService.bundleType === BundleTypeEnum.PARTS_BUNDLING;
    }

    constructor(private selectedTabsService: SelectedTabsService,
                private prettyPrintReferencePipe: PrettyPrintReferencePipe,
                private tableService: TableService<Enum, DataObject>,
                private host: ElementRef,
                private zone: NgZone) {
    }

    ngOnInit(): void {
        this.tableData = {
            labels: {
                labelsData: this.retrieveLabelTableData(this.retrieveBundleType),
                hasInnerBoldBorder: true
            },
            headers: {
                entries: this.retrieveHeadersTableData(this.retrieveBundleType)
            },
            expandableLines: false
        };
    }

    ngOnDestroy(): void {
        this.tableService
                .setOpenLineHeightValue({
                    identifier: `${this.bundleSettlementDetailData.settlementCode}-${this.bundleSettlementDetailData.complaintCode}`,
                    height: tableLineConfiguration.tableLineHeight
                });
        this.resizeObserver.unobserve(this.host.nativeElement);
    }

    ngAfterViewInit(): void {
        this.retrieveTableData();
        this.resizeObserver = new ResizeObserver((entries) => {
            this.zone.run(() => {
                this.tableService.setOpenLineHeightValue({
                    identifier: `${this.bundleSettlementDetailData.settlementCode}-${this.bundleSettlementDetailData.complaintCode}`,
                    height: entries[0].contentRect.height + tableLineConfiguration.tableLineHeight + this.paddingBox
                });
            });
        });
        this.resizeObserver.observe(this.host.nativeElement);
    }

    retrieveShipToPartyPipe(shipToParty: ShipToPartyBaseModel): string {
        if (shipToParty?.name) {
            return this.prettyPrintReferencePipe.transform(shipToParty);
        } else {
            return this.prettyPrintReferencePipe.transform(shipToParty, false, true);
        }
    }

    tooltipCommentText(comment: string): string {
        return comment != null ? this.tooltipComment : this.tooltipNoComment;
    }

    financialNumberPipe(data: number, currency: CurrencyBaseModel): string {
        const value = {
            data,
            currency
        } as FinancialNumberCellValueModel;
        return financialDataPipes.financialNumberPipe(value);
    }

    private retrieveLabelTableData(bundleType: BundleTypeEnum): string[] {
        const labels: string[] = [];
        switch (bundleType) {
            case BundleTypeEnum.PARTS_BUNDLING:
                labels.push('Part(s) block');
                break;
            case BundleTypeEnum.TE_MINUTES_BUNDLING:
                labels.push('TE minutes block');
                break;
        }
        labels.push('Identity block');
        return labels;
    }

    private retrieveHeadersTableData(bundleType: BundleTypeEnum): any {
        let headers: Map<BundleSettlementsDetailColumnEnum, TableHeaderEntryModel<BundleSettlementsDetailColumnEnum>>
                = new Map<BundleSettlementsDetailColumnEnum, TableHeaderEntryModel<BundleSettlementsDetailColumnEnum>>([]);

        switch (bundleType) {
            case BundleTypeEnum.PARTS_BUNDLING:
                headers = new Map<BundleSettlementsDetailColumnEnum, TableHeaderEntryModel<BundleSettlementsDetailColumnEnum>>([
                    [BundleSettlementsDetailColumnEnum.QUANTITY, bundleSettlementsDetailTable.quantity],
                    [BundleSettlementsDetailColumnEnum.MATERIALS_UNIT_NET_COST, bundleSettlementsDetailTable.materialsUnitNetCost],
                    [BundleSettlementsDetailColumnEnum.MATERIALS_TOTAL_NET_COST, bundleSettlementsDetailTable.materialsTotalNetCost],
                    [BundleSettlementsDetailColumnEnum.TAX_RATE, bundleSettlementsDetailTable.taxRate],
                    [BundleSettlementsDetailColumnEnum.MATERIALS_TOTAL_GROSS_COST, bundleSettlementsDetailTable.materialsTotalGrossCost]
                ]);
                break;
            case BundleTypeEnum.TE_MINUTES_BUNDLING:
                headers = new Map<BundleSettlementsDetailColumnEnum, TableHeaderEntryModel<BundleSettlementsDetailColumnEnum>>([
                    [BundleSettlementsDetailColumnEnum.TE_AMOUNT, bundleSettlementsDetailTable.teMinutesAmount],
                    [BundleSettlementsDetailColumnEnum.TE_UNIT_COST, bundleSettlementsDetailTable.teMinutesUnitCost],
                    [BundleSettlementsDetailColumnEnum.TE_ADDITIONAL_COSTS, bundleSettlementsDetailTable.teMinutesAdditionalCost],
                    [BundleSettlementsDetailColumnEnum.TE_TOTAL_COST, bundleSettlementsDetailTable.teMinutesTotalCost]
                ]);
                break;
        }
        headers.set(BundleSettlementsDetailColumnEnum.BLUE_FAPIAO_NUMBER, bundleSettlementsDetailTable.blueFapiaoNumber);
        headers.set(BundleSettlementsDetailColumnEnum.DELIVERY_NUMBER, bundleSettlementsDetailTable.deliveryNumber);
        headers.set(BundleSettlementsDetailColumnEnum.SALES_DOCUMENT, bundleSettlementsDetailTable.salesDocument);
        headers.set(BundleSettlementsDetailColumnEnum.COMMENTS, bundleSettlementsDetailTable.comments);
        return headers;
    }

    private retrieveTableData(): void {
        this.tableData.staticSection = this.mapData(SettlementPositionMapper
                .fromSettlementPositionBaseContractArray(this.bundleSettlementDetailData?.settlementPositions,
                        this.bundleSettlementDetailData?.currency), this.retrieveBundleType);
    }

    private mapData(bundleSettlementDetailData: SettlementPositionModel[], bundleType: BundleTypeEnum):
            TableLineModel<BundleSettlementsDetailColumnEnum, SettlementPositionModel>[] {
        return bundleSettlementDetailData?.filter((item) => item != null
                && (bundleType === BundleTypeEnum.PARTS_BUNDLING && item.materialsTotalGrossCost > 0
                        || bundleType === BundleTypeEnum.TE_MINUTES_BUNDLING && item.teMinutesTotalCost > 0))
                .map((line) => ({
                    isOpen: false,
                    data: this.mapLineData(line, bundleType)
                } as TableLineModel<BundleSettlementsDetailColumnEnum, SettlementPositionModel>)) ?? [];
    };

    private mapLineData(line: SettlementPositionModel, bundleType: BundleTypeEnum):
            Map<BundleSettlementsDetailColumnEnum, TableLineDataModel> {
        let map: Map<BundleSettlementsDetailColumnEnum, TableLineDataModel>
                = new Map<BundleSettlementsDetailColumnEnum, TableLineDataModel>([]);

        switch (bundleType) {
            case BundleTypeEnum.PARTS_BUNDLING:
                map = new Map<BundleSettlementsDetailColumnEnum, TableLineDataModel>([
                    [BundleSettlementsDetailColumnEnum.QUANTITY, {cellValue: line?.quantity}],
                    [
                        BundleSettlementsDetailColumnEnum.MATERIALS_UNIT_NET_COST, {
                        cellValue: {
                            data: line?.materialsUnitNetCost,
                            currency: line?.currency
                        }
                    }
                    ],
                    [
                        BundleSettlementsDetailColumnEnum.MATERIALS_TOTAL_NET_COST, {
                        cellValue: {
                            data: line?.materialsTotalNetCost,
                            currency: line?.currency
                        }
                    }
                    ],
                    [BundleSettlementsDetailColumnEnum.TAX_RATE, {cellValue: line?.taxRate}],
                    [
                        BundleSettlementsDetailColumnEnum.MATERIALS_TOTAL_GROSS_COST, {
                        cellValue: {
                            data: line?.materialsTotalGrossCost,
                            currency: line?.currency
                        }
                    }
                    ]
                ]);
                break;
            case BundleTypeEnum.TE_MINUTES_BUNDLING:
                map = new Map<BundleSettlementsDetailColumnEnum, TableLineDataModel>([
                    [BundleSettlementsDetailColumnEnum.TE_AMOUNT, {cellValue: line?.teMinutesAmount}],
                    [
                        BundleSettlementsDetailColumnEnum.TE_UNIT_COST, {
                        cellValue: {
                            data: line?.teMinutesUnitCost,
                            currency: line?.currency
                        }
                    }
                    ],
                    [
                        BundleSettlementsDetailColumnEnum.TE_ADDITIONAL_COSTS, {
                        cellValue: {
                            data: line?.teMinutesAdditionalCost,
                            currency: line?.currency
                        }
                    }
                    ],
                    [
                        BundleSettlementsDetailColumnEnum.TE_TOTAL_COST, {
                        cellValue: {
                            data: line?.teMinutesTotalCost,
                            currency: line?.currency
                        }
                    }
                    ]
                ]);
                break;
        }

        map.set(BundleSettlementsDetailColumnEnum.BLUE_FAPIAO_NUMBER, {cellValue: line?.blueFapiaoNumber});
        map.set(BundleSettlementsDetailColumnEnum.DELIVERY_NUMBER, {cellValue: line?.deliveryNumber});
        map.set(BundleSettlementsDetailColumnEnum.SALES_DOCUMENT, {cellValue: line?.salesDocument});
        map.set(BundleSettlementsDetailColumnEnum.COMMENTS, {
            cellValue: {
                templateRef: this.commentTableTemplate,
                context: {
                    commentTooltip: this.tooltipCommentText(line?.comments),
                    commentValue: line?.comments,
                    commentIsNull: line?.comments == null
                }
            }
        });
        return map;
    }
}
